import React from "react";
import { Trophy } from "lucide-react";
import { Reveal, useScrollThreshold } from "../hooks";
import { MARKETING_EXAM_PREVIEW } from "../examPreview";

export function PlatformShowcase() {
  const [devicesRef, active] = useScrollThreshold(0.65);

  const ease = "cubic-bezier(.16,1,.3,1)";
  const dur = "0.7s";

  return (
    <section
      className="py-36 px-4 overflow-hidden"
      data-testid="platform-showcase"
      style={{
        background:
          "linear-gradient(180deg, hsl(220 30% 97%) 0%, hsl(0 0% 100%) 100%)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-cabinet font-black tracking-tight leading-tight">
              Built from scratch for
              <br />
              <span className="text-primary">how you actually study.</span>
            </h2>
            <p className="text-lg text-muted-foreground font-plex mt-5 max-w-xl mx-auto">
              Not a reskin of an old platform. A modern UCAT experience with
              analytics at its core.
            </p>
          </div>
        </Reveal>

        <div
          ref={devicesRef}
          className="relative max-w-5xl mx-auto"
          style={{ perspective: "1800px", minHeight: "560px" }}
        >
          <div
            className="relative z-10 mx-auto"
            style={{
              maxWidth: "780px",
              transform: active
                ? "scale(1) rotateX(0deg) rotateY(0deg)"
                : "scale(0.55) rotateX(18deg) rotateY(-6deg)",
              opacity: active ? 1 : 0,
              transition: `transform ${dur} ${ease}, opacity 0.4s ease`,
              transformOrigin: "center 70%",
              transformStyle: "preserve-3d",
            }}
          >
            <div
              className="rounded-t-xl p-[6px] border border-[#222]"
              style={{
                background: "linear-gradient(170deg, #1e1e1e 0%, #111 100%)",
                boxShadow: active
                  ? "0 40px 100px -25px rgba(0,0,0,0.3), 0 15px 40px -15px rgba(0,0,0,0.18)"
                  : "0 10px 30px -10px rgba(0,0,0,0.1)",
                transition: `box-shadow ${dur} ${ease}`,
              }}
            >
              <div className="flex justify-center mb-[3px]">
                <div className="w-[5px] h-[5px] rounded-full bg-[#444]" />
              </div>
              <div
                className="rounded-[3px] overflow-hidden bg-white"
                style={{ aspectRatio: "16 / 10" }}
              >
                <img
                  src={MARKETING_EXAM_PREVIEW.src}
                  alt={MARKETING_EXAM_PREVIEW.alt}
                  className="w-full h-full"
                  style={{ objectFit: "cover", objectPosition: "top" }}
                  draggable={false}
                  loading="lazy"
                />
              </div>
            </div>
            <div
              className="h-[5px] mx-16 rounded-b"
              style={{
                background: "linear-gradient(180deg, #222 0%, #111 100%)",
              }}
            />
            <div
              className="h-[12px] rounded-b-xl mx-1"
              style={{
                background: "linear-gradient(180deg, #2a2a2a 0%, #111 100%)",
                boxShadow: "0 6px 16px -4px rgba(0,0,0,0.25)",
              }}
            >
              <div
                className="w-14 h-[3px] bg-[#333] rounded-full mx-auto"
                style={{ marginTop: "3px" }}
              />
            </div>
          </div>

          <div
            className="absolute z-20"
            style={{
              right: "20px",
              bottom: "40px",
              transform: active
                ? "translateX(0) translateY(0) rotate(0deg)"
                : "translateX(120px) translateY(70px) rotate(5deg)",
              opacity: active ? 1 : 0,
              transition: `transform ${dur} ${ease} 0.08s, opacity 0.4s ease 0.08s`,
            }}
          >
            <div
              className="rounded-[26px] border border-[#444]"
              style={{
                width: "150px",
                padding: "8px 6px",
                background: "linear-gradient(170deg, #2a2a2a 0%, #1a1a1a 100%)",
                boxShadow:
                  "0 30px 70px -18px rgba(0,0,0,0.35), 0 10px 25px -8px rgba(0,0,0,0.2)",
              }}
            >
              <div className="flex justify-center mb-1">
                <div className="w-[38px] h-[10px] bg-[#111] rounded-full" />
              </div>
              <div
                className="bg-white rounded-[16px] overflow-hidden"
                style={{ height: "280px" }}
              >
                <div className="px-2.5 py-2 flex items-center justify-between bg-white border-b border-gray-100">
                  <div
                    className="text-[9px]"
                    style={{ display: "flex", alignItems: "baseline" }}
                  >
                    <span
                      className="font-black"
                      style={{
                        fontFamily: "Cabinet Grotesk, sans-serif",
                        color: "#0A0A0A",
                      }}
                    >
                      Med
                    </span>
                    <span
                      style={{
                        fontFamily: "IBM Plex Mono, monospace",
                        fontWeight: 300,
                        color: "#666",
                      }}
                    >
                      900
                    </span>
                    <span
                      style={{
                        color: "#0055FF",
                        fontFamily: "Cabinet Grotesk, sans-serif",
                        fontWeight: 900,
                      }}
                    >
                      .
                    </span>
                  </div>
                  <div className="w-5 h-5 rounded-full bg-[#0A0A0A] flex items-center justify-center">
                    <span className="text-white text-[6px] font-bold">T</span>
                  </div>
                </div>
                <div className="p-2.5">
                  <div
                    className="text-[8px] font-bold text-gray-800 mb-2"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                  >
                    Practice
                  </div>
                  {[
                    {
                      code: "VR",
                      name: "Verbal Reasoning",
                      pct: 65,
                      color: "#3B82F6",
                    },
                    {
                      code: "DM",
                      name: "Decision Making",
                      pct: 42,
                      color: "#10B981",
                    },
                    {
                      code: "QR",
                      name: "Quantitative Reasoning",
                      pct: 28,
                      color: "#8B5CF6",
                    },
                    {
                      code: "SJT",
                      name: "Situational Judgement",
                      pct: 15,
                      color: "#F97316",
                    },
                  ].map((s) => (
                    <div
                      key={s.code}
                      className="flex items-center gap-1.5 mb-2"
                    >
                      <div
                        className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center text-white text-[5px] font-bold"
                        style={{ backgroundColor: s.color }}
                      >
                        {s.code}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-[6px] text-gray-700 truncate"
                          style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
                        >
                          {s.name}
                        </div>
                        <div className="w-full h-[3px] bg-gray-100 rounded-full mt-0.5">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${s.pct}%`,
                              backgroundColor: s.color,
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-[5px] text-gray-400 font-mono">
                        {s.pct}%
                      </span>
                    </div>
                  ))}
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <div
                      className="text-[7px] font-bold text-gray-800 mb-1.5"
                      style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                    >
                      Performance
                    </div>
                    <div className="flex items-end gap-[2px] h-10">
                      {[30, 40, 35, 55, 50, 65, 60, 70, 75, 68, 80, 85].map(
                        (h, i) => (
                          <div
                            key={i}
                            className="flex-1 rounded-t"
                            style={{
                              height: `${h}%`,
                              backgroundColor: i >= 10 ? "#0055FF" : "#e5e7eb",
                            }}
                          />
                        ),
                      )}
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[5px] text-gray-400 font-mono">
                        Week 1
                      </span>
                      <span className="text-[5px] text-gray-400 font-mono">
                        Week 12
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 bg-gray-50 rounded-lg p-1.5 flex items-center justify-between">
                    <span
                      className="text-[6px] text-gray-500"
                      style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
                    >
                      Est. Score
                    </span>
                    <span
                      className="text-[9px] font-black font-mono"
                      style={{ color: "#0055FF" }}
                    >
                      2,620
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center mt-1">
                <div className="w-10 h-[3px] bg-white/20 rounded-full" />
              </div>
            </div>
          </div>

          <div
            className="absolute z-20"
            style={{
              left: "-20px",
              bottom: "70px",
              transform: active
                ? "translateX(0) translateY(0)"
                : "translateX(-70px) translateY(50px)",
              opacity: active ? 1 : 0,
              transition: `transform ${dur} ${ease} 0.12s, opacity 0.4s ease 0.12s`,
            }}
          >
            <div className="space-y-3">
              <div
                className="bg-white rounded-2xl p-3.5 flex items-center gap-3 w-52"
                style={{
                  boxShadow:
                    "0 14px 45px -10px rgba(0,0,0,0.1), 0 4px 14px -6px rgba(0,0,0,0.05)",
                }}
              >
                <div className="w-9 h-9 rounded-xl bg-blue-500 text-white flex items-center justify-center text-xs font-bold font-mono">
                  VR
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground font-plex">
                    Verbal Reasoning
                  </div>
                  <div className="text-sm font-black font-mono text-foreground">
                    820{" "}
                    <span className="text-emerald-500 text-[10px]">+90</span>
                  </div>
                </div>
              </div>
              <div
                className="bg-white rounded-2xl p-3.5 w-52"
                style={{
                  boxShadow:
                    "0 14px 45px -10px rgba(0,0,0,0.1), 0 4px 14px -6px rgba(0,0,0,0.05)",
                }}
              >
                <div className="text-[10px] text-muted-foreground font-plex mb-2">
                  Score Trend
                </div>
                <div className="flex items-end gap-[3px] h-9">
                  {[30, 35, 28, 45, 50, 55, 48, 62, 70, 68, 75, 82].map(
                    (h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t"
                        style={{
                          height: `${h}%`,
                          backgroundColor: i >= 10 ? "#0055FF" : "#e5e7eb",
                        }}
                      />
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className="absolute z-20"
            style={{
              right: "60px",
              top: "-15px",
              transform: active ? "translateY(0)" : "translateY(-60px)",
              opacity: active ? 1 : 0,
              transition: `transform ${dur} ${ease} 0.16s, opacity 0.4s ease 0.16s`,
            }}
          >
            <div
              className="bg-white rounded-2xl p-3.5 flex items-center gap-2.5"
              style={{
                boxShadow:
                  "0 14px 45px -10px rgba(0,0,0,0.1), 0 4px 14px -6px rgba(0,0,0,0.05)",
              }}
            >
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Trophy className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground font-plex">
                  Target Score
                </div>
                <div className="text-sm font-black font-mono text-primary">
                  Set & track
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
